import { Trans } from '@lingui/macro';
import { assertUnreachable } from '@luminovo/commons';
import { colorSystem, Flexbox, Row, SecondaryIconButton, Tooltip } from '@luminovo/design-system';
import { isOtsComponentFull, OtsOrCustomComponent, PreferenceStatusEnum } from '@luminovo/http-client';
import { LifecycleTooltip } from '@luminovo/sourcing-core';
import { Add, Info } from '@mui/icons-material';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import { useCallback } from 'react';
import { PartSuggestionAvailabilityView } from '../../../../components/PartAvailabilityView';
import {
    CpnView,
    RenderDescription,
    RenderIpn,
    RenderManufacturer,
    RenderMpn,
    RenderPackage,
    StickyTableCell,
} from '../../../../components/partColumns';
import { StickyRightHeaderTableCell } from '../../../../components/partColumns/StickyHeadTableCell';
import { TableCell } from '../../../../components/partColumns/TableCell';
import { PartComplianceView } from '../../../../components/PartComplianceView';
import { PartLifecycleView } from '../../../../components/PartLifecycleView';
import { designatorQtyMismatchTooltip } from '../AddParts/hasDesignatorOrQtyMismatch';
import { ManufacturerTooltip } from '../AddParts/PartialMatches/ManufacturerTooltip';
import { useAddPartWithBlockedManufacturerDialog } from '../AddParts/useAddPartWithBlockedManufacturerDialog';
import { ErpSearchTableContext } from './erpSearchTypes';

export const columnIpn = {
    id: `erp-search-ipn`,
    label: <Trans>IPN</Trans>,
    render: ({ data }: Row<OtsOrCustomComponent>): JSX.Element => {
        return <RenderIpn part={data.data} />;
    },
};

export const columnCpn = {
    id: `erp-search-cpn-rev`,
    label: <Trans>CPN • Rev</Trans>,
    render: ({ data }: Row<OtsOrCustomComponent>): JSX.Element => {
        return (
            <TableCell>
                <CpnView part={data.data} />
            </TableCell>
        );
    },
};

export const columnMpn = {
    id: `erp-search-mpn`,
    label: <Trans>MPN</Trans>,
    render: (
        { data }: Row<OtsOrCustomComponent>,
        { rfqContext, openOtsDrawer, openGenericPartDrawer }: ErpSearchTableContext,
    ): JSX.Element => {
        return (
            <RenderMpn
                part={data.data}
                rfqContext={rfqContext}
                openOtsDrawer={openOtsDrawer}
                openGenericPartDrawer={openGenericPartDrawer}
            />
        );
    },
};

export const columnManufacturer = {
    id: `erp-search-manufacturer`,
    label: (
        <Flexbox alignItems={'center'}>
            <Trans>Manufacturer</Trans>
            <ManufacturerTooltip>
                <Info style={{ color: colorSystem.neutral[5], height: 16 }} />
            </ManufacturerTooltip>
        </Flexbox>
    ),
    render: ({ data }: Row<OtsOrCustomComponent>): JSX.Element => {
        return <RenderManufacturer part={data.data} />;
    },
};

export const columnDescription = {
    id: `erp-search-description`,
    label: <Trans>Description</Trans>,
    render: ({ data }: Row<OtsOrCustomComponent>): JSX.Element => {
        return <RenderDescription part={data.data} />;
    },
};

export const columnPackage = {
    id: `erp-search-package`,
    label: <Trans>Package</Trans>,
    render: ({ data }: Row<OtsOrCustomComponent>): JSX.Element => {
        return <RenderPackage part={data.data} />;
    },
};

export const columnAvailability = {
    id: `erp-search-availability`,
    label: <Trans>Availability</Trans>,
    render: (
        { data }: Row<OtsOrCustomComponent>,
        { rfqId, bomItem, assemblyId }: ErpSearchTableContext,
    ): JSX.Element => {
        const componentType = data.type;
        const isComponentRemoved = data.data.state === 'Removed';
        if (componentType === 'Custom') {
            return <TableCell isComponentRemoved={isComponentRemoved}>-</TableCell>;
        }
        if (componentType === 'OffTheShelf') {
            return (
                <TableCell>
                    <PartSuggestionAvailabilityView
                        assemblyId={assemblyId}
                        rfqId={rfqId}
                        bomItem={bomItem}
                        part={data.data}
                        disabled={isComponentRemoved}
                    />
                </TableCell>
            );
        }
        assertUnreachable(componentType);
    },
};

export const columnLifecycle = {
    id: `erp-search-lifecycle`,
    label: (
        <Flexbox alignItems="center">
            <Trans>Lifecycle</Trans>
            <LifecycleTooltip>
                <Info style={{ color: colorSystem.neutral[5], height: 16 }} />
            </LifecycleTooltip>
        </Flexbox>
    ),
    render: ({ data }: Row<OtsOrCustomComponent>): JSX.Element => {
        const componentType = data.type;
        const isComponentRemoved = data.data.state === 'Removed';
        if (componentType === 'Custom') {
            return <TableCell isComponentRemoved={isComponentRemoved}>-</TableCell>;
        }
        if (componentType === 'OffTheShelf') {
            return (
                <TableCell>
                    <PartLifecycleView part={data.data} disabled={isComponentRemoved} />
                </TableCell>
            );
        }
        assertUnreachable(componentType);
    },
};

export const columnCompliance = {
    id: `erp-search-compliance`,
    label: <Trans>Compliance</Trans>,
    render: ({ data }: Row<OtsOrCustomComponent>, { assemblyIndustry }: ErpSearchTableContext): JSX.Element => {
        const componentType = data.type;
        const isComponentRemoved = data.data.state === 'Removed';
        if (componentType === 'Custom') {
            return <TableCell isComponentRemoved={isComponentRemoved}>-</TableCell>;
        }
        if (componentType === 'OffTheShelf') {
            return (
                <TableCell>
                    <PartComplianceView
                        part={data.data}
                        showAecq={assemblyIndustry === 'Auto'}
                        disabled={isComponentRemoved}
                    />
                </TableCell>
            );
        }
        assertUnreachable(componentType);
    },
};

const RenderActionButtons = ({
    component,
    isDisabled,
    handleAddPart,
    handleRemovePart,
    existingPartIds,
    hasDesignatorQuantityMismatch,
}: {
    component: OtsOrCustomComponent;
    isDisabled: boolean;
    handleAddPart: (component: OtsOrCustomComponent) => void;
    handleRemovePart: (partId: string) => void;
    existingPartIds: string[] | undefined;
    hasDesignatorQuantityMismatch: boolean;
}) => {
    const { openDialog: openBlockedManuDialog } = useAddPartWithBlockedManufacturerDialog();

    const handleAdd = useCallback(() => {
        if (
            isOtsComponentFull(component.data) &&
            component.data.manufacturer_preference_status === PreferenceStatusEnum.Blocked
        ) {
            return openBlockedManuDialog({
                partsWithBlockedManufacturers: [component.data],
                onConfirm: () => handleAddPart(component),
            });
        }

        return handleAddPart(component);
    }, [component, handleAddPart, openBlockedManuDialog]);

    return existingPartIds?.includes(component.data.id) ? (
        <Tooltip title={hasDesignatorQuantityMismatch ? designatorQtyMismatchTooltip() : ''}>
            <span>
                <SecondaryIconButton
                    disabled={isDisabled || hasDesignatorQuantityMismatch}
                    disableTouchRipple
                    onClick={(e) => {
                        e.stopPropagation();
                        handleRemovePart(component.data.id);
                    }}
                    size="small"
                >
                    <RemoveRoundedIcon fontSize="inherit" />
                </SecondaryIconButton>
            </span>
        </Tooltip>
    ) : (
        <Tooltip title={hasDesignatorQuantityMismatch ? designatorQtyMismatchTooltip() : ''}>
            <SecondaryIconButton
                data-testid={'add-ots-component-button'}
                disabled={isDisabled || hasDesignatorQuantityMismatch}
                disableTouchRipple
                onClick={(e) => {
                    e.stopPropagation();
                    handleAdd();
                }}
                size="small"
                style={{ pointerEvents: 'auto' }}
            >
                <Add fontSize="inherit" />
            </SecondaryIconButton>
        </Tooltip>
    );
};

export const columnActions = {
    id: `erp-search-actions`,
    label: <Trans>Actions</Trans>,
    overrides: {
        HeaderTableCell: StickyRightHeaderTableCell,
    },
    render: (
        { data }: Row<OtsOrCustomComponent>,
        {
            isSubmitting,
            existingPartIds,
            handleAddPart,
            handleRemovePart,
            hasDesignatorQuantityMismatch,
        }: ErpSearchTableContext,
    ): JSX.Element => {
        const isDisabled = isSubmitting;

        return (
            <StickyTableCell>
                <RenderActionButtons
                    component={data}
                    isDisabled={isDisabled}
                    existingPartIds={existingPartIds}
                    handleAddPart={handleAddPart}
                    handleRemovePart={handleRemovePart}
                    hasDesignatorQuantityMismatch={hasDesignatorQuantityMismatch}
                />
            </StickyTableCell>
        );
    },
};
